import { useCallback, useState } from "react"

import styles from "./CompanySmsSendingRuleMasterTab.module.scss"

import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useSouhlAdminQuery } from "@/provider/apollo/Apollo"
import SummaryTextModal from "@/souhl_admin/components/features/companies/sms-sending-rule/SummaryTextModal/SummaryTextModal"
import CompanySmsSendingRuleEditV2 from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleEditV2/CompanySmsSendingRuleEditV2"
import CompanySmsSendingRuleV2 from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleV2/CompanySmsSendingRuleV2"
import {
	companySmsSendingRuleModalMode,
	CompanySmsSendingRuleModalMode,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/const"
import { graphql } from "@/souhl_admin/gql/__generated__"

const getCompanySmsSendingRulesQuery = graphql(/* GraphQL */ `
	query getCompanySmsSendingRulesV2($companyId: String!) {
		companySmsSendingRulesV2(companyId: $companyId) {
			summaryText
			rules {
				id
				name
				...companySmsSendingRuleV2ViewFragment
			}
		}
	}
`)

type Props = {
	companyId: string
}

const CompanySmsSendingRuleMasterTab = ({ companyId }: Props) => {
	const [activeRuleIndex, setActiveRuleIndex] = useState(0)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [modalMode, setModalMode] = useState<
		Extract<
			CompanySmsSendingRuleModalMode,
			| typeof companySmsSendingRuleModalMode.EDIT
			| typeof companySmsSendingRuleModalMode.DUPLICATE_AND_CREATE
		>
	>(companySmsSendingRuleModalMode.EDIT)

	const { data, refetch } = useSouhlAdminQuery(getCompanySmsSendingRulesQuery, {
		variables: { companyId },
		fetchPolicy: "cache-first",
	})

	const rules = data?.companySmsSendingRulesV2.rules || []
	// 会社単位の店舗送付用テキスト (ルールごとに「適用店舗名 → そのルールの summaryText」)
	const companySummaryText = data?.companySmsSendingRulesV2.summaryText || ""

	const onModalOpenChange = useCallback(() => {
		setIsModalOpen((prev) => !prev)
	}, [])

	const onCreateModalOpenChange = useCallback(() => {
		setIsCreateModalOpen((prev) => !prev)
	}, [])

	return (
		<>
			<div className={styles.tab_inner_content}>
				{rules.length > 0 ? (
					<>
						<div className={styles.rules_container}>
							<div className={styles.rules_switcher}>
								{rules.map((rule, idx) => (
									<button
										key={rule.id}
										type="button"
										className={
											idx === activeRuleIndex
												? `${styles.rule_button} ${styles.active}`
												: styles.rule_button
										}
										onClick={() => setActiveRuleIndex(idx)}
									>
										{rule.name}
									</button>
								))}
							</div>
							<div className={styles.button_group}>
								<SummaryTextModal
									summaryText={companySummaryText}
									triggerLabel="会社全体のテキストを確認"
									title="店舗送付用テキスト（会社全体）"
									triggerClassName={styles.summary_button}
								/>
								<button
									className={styles.edit_button}
									onClick={() => setIsCreateModalOpen(true)}
								>
									新規作成
								</button>
							</div>
						</div>

						{rules[activeRuleIndex] && (
							<CompanySmsSendingRuleV2
								fragment={rules[activeRuleIndex]}
								onEditCopyButtonClick={(mode) => {
									setModalMode(mode)
									setIsModalOpen(true)
								}}
							/>
						)}
					</>
				) : (
					<div>データはありません。</div>
				)}
			</div>
			<ModalDialog
				isOpen={isModalOpen}
				onOpenChange={onModalOpenChange}
				contentClassName={styles.modal_content}
			>
				{rules[activeRuleIndex] && (
					<CompanySmsSendingRuleEditV2
						companyId={companyId}
						ruleId={rules[activeRuleIndex].id}
						mode={modalMode}
						closeModal={() => setIsModalOpen(false)}
						onUpdated={() => {
							refetch()
						}}
					/>
				)}
			</ModalDialog>

			{/* Create Modal */}
			<ModalDialog
				isOpen={isCreateModalOpen}
				onOpenChange={onCreateModalOpenChange}
				contentClassName={styles.modal_content}
			>
				<CompanySmsSendingRuleEditV2
					companyId={companyId}
					mode={companySmsSendingRuleModalMode.CREATE}
					ruleIdForPolicyNamesFetch={rules[activeRuleIndex]?.id || rules[0]?.id}
					closeModal={() => setIsCreateModalOpen(false)}
					onUpdated={() => {
						refetch()
					}}
				/>
			</ModalDialog>
		</>
	)
}

export default CompanySmsSendingRuleMasterTab
